import * as React from "react";
import {
  useAuthenticate,
  useInitializeOfflineData,
  useOnline,
  useSelector,
  useToast,
  dbQueries
} from "duck";
import { useLogPinLoginsMutation } from "requests";
import { Context, Types } from "./duck";

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [config, setConfig] = React.useState(({
    loading: true,
    user: null
  } as unknown) as Types.Config);

  const userId = config.user?._id;
  const online = useOnline();
  const hasUnpublishedChanges = useSelector(({ cases }) => {
    if (!userId) {
      return false;
    }
    return (
      Object.values(cases).filter(
        medCase =>
          medCase.userId === userId &&
          (medCase.status === "offline" ||
            medCase.status === "has_offline_changes")
      ).length > 0
    );
  });

  const toast = useToast();
  const [sendLogs] = useLogPinLoginsMutation({ ignoreResults: true });

  React.useEffect(() => {
    if (!userId) {
      return;
    }

    if (hasUnpublishedChanges && online) {
      toast({
        status: "info",
        description:
          "You have unpublished cases. You can publish them from the Cases list."
      });
    }
    if (online) {
      dbQueries
        .getLogs()
        .then(logs => sendLogs({ variables: { input: { pinLogins: logs } } }))
        .then(() => dbQueries.removeLogs())
        .catch(() => {});
    }
  }, [userId, online]);

  const { initOfflineData } = useInitializeOfflineData();

  useAuthenticate({ setConfig, onCompleted: initOfflineData });

  const context: Types.ContextValue = React.useMemo(() => [config, setConfig], [
    config
  ]);

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default AuthProvider;
